<template>
  <div ref="root" class="relative lg:w-64 md:w-44 w-28">
    <button
      type="button"
      id="agencies"
      class="flex w-full min-h-[38px] items-center justify-between box-border border-solid border border-gray-300 rounded-md shadow-sm bg-white px-3 py-1.5 text-left focus:border-gtGray500 focus:outline-none focus:ring-1 focus:ring-gtGray500"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      aria-controls="user-agency-state-picker-panel"
      aria-label="Select agencies"
      @click="toggleOpen"
    >
      <span v-if="selectedAgencies.length" class="font-normal truncate">
        {{ selectedAgencies.length }} selected
      </span>
      <span v-else class="font-normal truncate">Select agency</span>
      <svg
        class="ml-2 h-4 w-4 shrink-0 text-gray-500 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      ref="panel"
      id="user-agency-state-picker-panel"
      class="absolute top-full left-0 z-50 mt-1 md:w-96 w-72 rounded-lg border border-gtGray300 bg-white p-3 shadow-lg"
      role="listbox"
      aria-label="Agencies by state"
      @keydown.esc.stop="close"
    >
        <SearchBar v-model:searchQuery="searchQuery" classes="w-full" />

        <div v-if="!visibleStateGroups.length" class="mt-3 text-sm text-gtGray700">
          {{ emptyMessage }}
        </div>

        <div
          v-else
          class="mt-3 max-h-48 sm:max-h-60 lg:max-h-72 overflow-y-auto overflow-x-hidden divide-y divide-gray-200"
          role="list"
        >
          <div v-for="group in visibleStateGroups" :key="group.key" class="py-2" role="listitem">
            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                class="h-4 w-4 shrink-0 rounded border-gray-300 text-buttonBlue focus:ring-buttonBlue cursor-pointer"
                :data-state-key="group.key"
                :checked="group.checked"
                :aria-checked="group.indeterminate ? 'mixed' : group.checked"
                :aria-label="isSearchActive ? `Select all matching agencies in ${group.name}` : `Select all agencies in ${group.name}`"
                :ref="(el) => syncStateCheckbox(el, group)"
                @change="onStateToggle(group, $event)"
              />

              <button
                type="button"
                class="flex flex-1 items-center justify-between text-left text-gtGray800 hover:text-buttonBlue focus:outline-none"
                :aria-expanded="isExpanded(group.key)"
                @click="toggleExpand(group.key)"
              >
                <span class="text-sm font-medium">
                  {{ group.name }}
                  <span class="font-normal text-gtGray700"> ({{ group.selectedCount }} of {{ group.totalCount }}) </span>
                </span>
                <svg
                  class="h-4 w-4 shrink-0 text-gtGray700 transition-transform"
                  :class="{ 'rotate-180': isExpanded(group.key) }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <ul v-if="isExpanded(group.key)" class="mt-2 ml-6 space-y-1">
              <li v-for="agency in group.displayAgencies" :key="agency.value">
                <button
                  type="button"
                  class="flex w-full min-w-0 flex-1 truncate items-center px-1 py-1.5 text-sm text-left rounded cursor-pointer hover:bg-gray-50"
                  :class="isAgencySelected(agency.value) ? 'text-buttonBlue' : 'text-gtGray800'"
                  @click="toggleAgency(agency.value)"
                >
                  <svg
                    v-if="isAgencySelected(agency.value)"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 mr-2 shrink-0 text-buttonBlue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span v-else class="inline-block w-5 h-5 mr-2 shrink-0" aria-hidden="true" />
                  {{ agency.label }}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <p class="mt-3 border-t border-gray-200 pt-2 text-sm text-gtGray700">
          {{ selectedAgencies.length }} selected
          <span v-if="selectedStateCount > 0">
            across {{ selectedStateCount }} {{ selectedStateCount === 1 ? 'state' : 'states' }}
          </span>
        </p>
    </div>
  </div>
</template>

<script>
import SearchBar from '@/views/Shared/SearchBar.vue';

const OTHER_KEY = '__OTHER__';

export default {
  name: 'UserAgencyStatePicker',
  components: { SearchBar },
  props: {
    agencyOptions: {
      type: Array,
      default: () => [],
    },
    states: {
      type: Array,
      default: () => [],
    },
    selectedAgencies: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      expandedKeys: {},
    };
  },
  computed: {
    selectedIdSet() {
      return new Set(this.selectedAgencies || []);
    },
    stateNameByAbbrev() {
      return Object.fromEntries((this.states || []).map((state) => [state.abbrev, state.name]));
    },
    isSearchActive() {
      return !!(this.searchQuery || '').trim();
    },
    stateGroups() {
      const byState = {};

      (this.agencyOptions || []).forEach((option) => {
        const key = option.stateAbbrev || OTHER_KEY;
        if (!byState[key]) {
          byState[key] = [];
        }
        byState[key].push(option);
      });

      return Object.keys(byState)
        .map((key) => {
          const agencies = [...byState[key]].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
          const selectedCount = agencies.filter((agency) =>
            this.selectedIdSet.has(agency.value),
          ).length;
          const totalCount = agencies.length;

          return {
            key,
            abbrev: key === OTHER_KEY ? null : key,
            name: key === OTHER_KEY ? 'Other' : this.stateNameByAbbrev[key] || key,
            agencies,
            agencyIds: agencies.map((agency) => agency.value),
            selectedCount,
            totalCount,
            checked: totalCount > 0 && selectedCount === totalCount,
            indeterminate: selectedCount > 0 && selectedCount < totalCount,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    visibleStateGroups() {
      const query = (this.searchQuery || '').trim().toLowerCase();

      if (!query) {
        return this.stateGroups.map((group) => this.buildVisibleGroup(group, group.agencies));
      }

      return this.stateGroups
        .map((group) => {
          const stateMatches =
            group.name.toLowerCase().includes(query) || (group.abbrev || '').toLowerCase().includes(query);
          const matchingAgencies = group.agencies.filter((agency) =>
            (agency.label || '').toLowerCase().includes(query),
          );

          if (!stateMatches && matchingAgencies.length === 0) {
            return null;
          }

          const displayAgencies = stateMatches ? group.agencies : matchingAgencies;
          return this.buildVisibleGroup(group, displayAgencies);
        })
        .filter(Boolean);
    },
    selectedStateCount() {
      return this.stateGroups.filter((group) => group.selectedCount > 0).length;
    },
    emptyMessage() {
      if ((this.agencyOptions || []).length === 0) {
        return 'No agencies available.';
      }
      if ((this.searchQuery || '').trim()) {
        return 'No agencies match your search.';
      }
      return 'No agencies available.';
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          this.focusSearchInput();
          this.syncAllStateCheckboxes();
        });
      }
    },
    selectedAgencies: {
      handler() {
        this.$nextTick(this.syncAllStateCheckboxes);
      },
      deep: true,
    },
    searchQuery() {
      this.$nextTick(this.syncAllStateCheckboxes);
    },
  },
  updated() {
    this.syncAllStateCheckboxes();
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  methods: {
    buildVisibleGroup(group, displayAgencies) {
      const agencyIds = displayAgencies.map((agency) => agency.value);
      const selectedCount = displayAgencies.filter((agency) => this.selectedIdSet.has(agency.value)).length;
      const totalCount = displayAgencies.length;

      return {
        ...group,
        displayAgencies,
        agencyIds,
        selectedCount,
        totalCount,
        checked: totalCount > 0 && selectedCount === totalCount,
        indeterminate: selectedCount > 0 && selectedCount < totalCount,
      };
    },
    toggleOpen() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
    focusSearchInput() {
      const panel = this.$refs.panel;
      const input = panel?.querySelector('#search');
      input?.focus();
    },
    onDocumentClick(event) {
      if (!this.isOpen) {
        return;
      }

      if (this.$refs.root?.contains(event.target)) {
        return;
      }

      this.close();
    },
    onDocumentKeydown(event) {
      if (this.isOpen && event.key === 'Escape') {
        this.close();
      }
    },
    syncStateCheckbox(el, group) {
      if (!el || !group) {
        return;
      }

      el.checked = group.checked;
      el.indeterminate = group.indeterminate;
    },
    syncAllStateCheckboxes() {
      if (!this.isOpen || !this.$refs.panel) {
        return;
      }

      this.visibleStateGroups.forEach((group) => {
        const el = this.$refs.panel.querySelector(`[data-state-key="${group.key}"]`);
        this.syncStateCheckbox(el, group);
      });
    },
    isExpanded(key) {
      return !!this.expandedKeys[key];
    },
    toggleExpand(key) {
      this.expandedKeys = {
        ...this.expandedKeys,
        [key]: !this.expandedKeys[key],
      };
    },
    isAgencySelected(agencyId) {
      return this.selectedIdSet.has(agencyId);
    },
    emitSelection(nextIds) {
      this.$emit('update', [...nextIds]);
    },
    onStateToggle(group, event) {
      if (event.target.checked) {
        this.selectState(group);
      } else {
        this.clearState(group);
      }

      this.$nextTick(this.syncAllStateCheckboxes);
    },
    selectState(group) {
      const next = new Set(this.selectedAgencies);
      group.agencyIds.forEach((id) => next.add(id));
      this.emitSelection(next);
    },
    clearState(group) {
      const remove = new Set(group.agencyIds);
      const next = this.selectedAgencies.filter((id) => !remove.has(id));
      this.emitSelection(next);
    },
    toggleAgency(agencyId) {
      if (this.selectedIdSet.has(agencyId)) {
        this.emitSelection(this.selectedAgencies.filter((id) => id !== agencyId));
      } else {
        this.emitSelection([...this.selectedAgencies, agencyId]);
      }
    },
  },
};
</script>
